/**
 * MarthaEditorContext — Shared state for the editor and its feature modules.
 * Provides editor instance, feature flags, and loading state to child components.
 */

import { createContext, useContext, type ReactNode } from 'react';
import type { BlockNoteEditor as BlockNoteEditorCore } from '@blocknote/core';

export interface MarthaEditorFeatures {
  collaboration: boolean;
  outline: boolean;
  comments: boolean;
  ai: boolean;
  mermaid: boolean;
  stats: boolean;
  breadcrumb: boolean;
}

export interface MarthaEditorContextValue {
  editor: BlockNoteEditorCore | null;
  features: MarthaEditorFeatures;
  readOnly: boolean;
  documentId?: string;
  projectId?: string;
  isLoading: boolean;
  loadProgress?: { loaded: number; total: number };
}

const MarthaEditorCtx = createContext<MarthaEditorContextValue | null>(null);

export function MarthaEditorProvider({
  value,
  children,
}: {
  value: MarthaEditorContextValue;
  children: ReactNode;
}) {
  return (
    <MarthaEditorCtx.Provider value={value}>
      {children}
    </MarthaEditorCtx.Provider>
  );
}

export function useMarthaEditorContext(): MarthaEditorContextValue {
  const ctx = useContext(MarthaEditorCtx);
  if (!ctx) {
    throw new Error('useMarthaEditorContext must be used within a MarthaEditorProvider');
  }
  return ctx;
}

export function useMarthaEditorOptional(): MarthaEditorContextValue | null {
  return useContext(MarthaEditorCtx);
}
